import { formatDateYMD, parseDateYMD } from 'utils/format-date'
import { DatePicker, type DatePickerProps } from './DatePicker'

export interface DateInputProps extends Omit<DatePickerProps, 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'minDate' | 'maxDate' | 'typeable'> {
    /** YYYY-MM-DD (an ISO timestamp is read by its date part), or '' while empty. */
    value: string | null | undefined
    /** YYYY-MM-DD, or '' while empty or incomplete. */
    onChange: (value: string) => void
    /** YYYY-MM-DD, like a native input's `min`. */
    min?: string
    /** YYYY-MM-DD, like a native input's `max`. */
    max?: string
    /** Focus left the field; the value is YYYY-MM-DD or ''. `incomplete`: something is typed but it is not a date (see DatePicker). */
    onBlur?: (value: string, state: { incomplete: boolean }) => void
}

const toValue = (date: Date | null): string => (date ? formatDateYMD(date) : '')

/**
 * The replacement for `<input type="date">`, with the same YYYY-MM-DD value. A native date input shows and takes the date in the browser's
 * language (дд.мм.гггг in a Russian browser) and no attribute changes that; this one is always typed as mm / dd / yyyy or picked from a calendar.
 */
export const DateInput = ({ value, onChange, onBlur, min, max, ...props }: DateInputProps) => (
    <DatePicker
        typeable
        nativeCalendar
        {...props}
        value={parseDateYMD(value)}
        minDate={parseDateYMD(min) ?? undefined}
        maxDate={parseDateYMD(max) ?? undefined}
        onChange={(date) => onChange(toValue(date))}
        onBlur={onBlur ? (date, state) => onBlur(toValue(date), state) : undefined}
    />
)
